'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';

// ─── Design tokens (matching site system) ─────────────────────────────────────
const CYAN = '#6FD3E0';
const ORANGE = '#FF8A3D';
const BG_DARK = '#07111C';
const BG_CARD = '#0F2234';
const BG_SECTION = '#0E1C2B';
const TEXT_PRIMARY = '#EAF4F8';
const TEXT_SECONDARY = '#C8DDE6';
const BORDER = 'rgba(111,211,224,0.12)';

// ─── Data ─────────────────────────────────────────────────────────────────────

const QUICK_BADGES = [
  { label: 'Depth', value: '16–42m', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><line x1="4" y1="20" x2="4" y2="4"/><line x1="4" y1="4" x2="20" y2="4"/><line x1="4" y1="12" x2="14" y2="12"/></svg> },
  { label: 'Access', value: 'Boat dive', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M3 17l1.5-6H20l1.5 6"/><path d="M3 17h18"/><path d="M5 17l-1 3h16l-1-3"/><path d="M8 11V7h8v4"/><path d="M12 7V4"/><path d="M10 4h4"/></svg> },
  { label: 'Level', value: 'Advanced (OW options)', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg> },
  { label: 'Visibility', value: '20–40m', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
  { label: 'Type', value: 'Wreck / Artificial Reef', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M3 17l1.5-6H20l1.5 6"/><path d="M3 17h18"/><path d="M5 17l-1 3h16l-1-3"/><path d="M8 11V7h8v4"/><path d="M12 7V4"/><path d="M10 4h4"/></svg> },
];

const WHY_DIVE = [
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    title: 'World-class wreck dive',
    desc: 'Consistently ranked in the global top 10 wreck dives',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
    title: 'Massive 172m structure',
    desc: 'One of the largest diveable wrecks in the Mediterranean',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    title: 'All skill levels welcome',
    desc: 'Shallow external routes for OW, deep penetration for tech divers',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    title: 'Exceptional visibility',
    desc: 'Typically 20–40m — ideal for photography and exploration',
  },
  {
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    title: 'Rich marine life',
    desc: 'Thriving artificial reef ecosystem with diverse species',
  },
];

const MARINE_LIFE = [
  { name: 'Groupers', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Barracuda', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Moray eels', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Sea bream', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Lionfish', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'Occasional turtles', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
];

const HIGHLIGHTS = [
  {
    title: 'Truck Cargo Deck',
    desc: 'Iconic rows of trucks frozen in time',
    image: '/assets/images/zenobia-wreck-dive-larnaca-cyprus-divers-1777008913772.jpg',
    alt: 'Divers exploring the truck cargo deck inside the Zenobia wreck in Larnaca Cyprus',
  },
  {
    title: 'The Propeller',
    desc: 'Massive structure at the seabed (~42m)',
    image: '/assets/images/zenobia-wreck-cyprus-scuba-diving-larnaca-1777197346785.jpg',
    alt: 'The massive propeller of the Zenobia wreck resting at 42m depth off Larnaca Cyprus',
  },
  {
    title: 'Bridge Area',
    desc: 'Exploration of upper structure and viewpoints',
    image: '/assets/images/larnaca-shore-diving-cyprus-shallow-reef-divers-1777197584083.jpg',
    alt: 'Divers exploring the bridge area of the Zenobia wreck with panoramic views',
  },
  {
    title: 'Swim-throughs',
    desc: 'Safe external routes with dramatic lighting',
    image: '/assets/images/openart-image_IqytUsMC_1769762135737_raw-1770563931345.jpg',
    alt: 'Diver swimming through an external swim-through on the Zenobia wreck with light beams',
  },
  {
    title: 'Light & Structure',
    desc: 'Sunlight beams cutting through the wreck',
    image: '/assets/images/openart-image_ENIQT6-4_1769928765025_raw-1769928829950.jpg',
    alt: 'Sunlight beams cutting through the Zenobia wreck structure creating dramatic underwater lighting',
  },
];

const PRACTICAL_INFO = [
  { label: 'Location', value: '~1.5 km off Larnaca coast', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg> },
  { label: 'Access', value: 'Boat dive (5–10 minutes)', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M3 17l1.5-6H20l1.5 6"/><path d="M3 17h18"/><path d="M5 17l-1 3h16l-1-3"/><path d="M8 11V7h8v4"/><path d="M12 7V4"/><path d="M10 4h4"/></svg> },
  { label: 'Best Season', value: 'April – November (year-round possible)', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> },
  { label: 'Water Temperature', value: '16–28°C depending on season', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg> },
  { label: 'Visibility', value: 'Typically 20–40m', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> },
];

const SAFETY_TIPS = [
  'Always dive with a local dive centre',
  'Use descent lines for controlled entry',
  'Monitor depth carefully — max depth 42m',
  'Carry a torch for penetration sections',
  'Respect penetration limits and your training level',
];

const CERT_CARDS = [
  {
    level: 'Open Water',
    color: CYAN,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M2 12c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/><path d="M2 17c1.5-3 4-4.5 6-4.5s4.5 1.5 6 1.5 4.5-1.5 6-4.5"/></svg>,
    desc: 'Shallow external exploration (guided)',
    detail: 'Explore the upper hull, swim-throughs and external structure down to 18m with a qualified guide.',
  },
  {
    level: 'Advanced',
    color: ORANGE,
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    desc: 'Full recreational access',
    detail: 'Access the full wreck including cargo decks, the propeller area and deeper sections to 30m.',
  },
  {
    level: 'Technical',
    color: '#a78bfa',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>,
    desc: 'Penetration and decompression dives',
    detail: 'Deep penetration into the interior, engine room and decompression dives beyond 40m.',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ZenobiaWreckPage() {
  const [hoveredHighlight, setHoveredHighlight] = useState<number | null>(null);

  return (
    <>
      <Header className="mb-0" />
      <main className="pt-16 lg:pt-20" style={{ background: BG_DARK, color: TEXT_PRIMARY, fontFamily: 'var(--font-body)' }}>

        {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
        <section className="relative min-h-[85vh] flex items-end overflow-hidden">
          {/* Background image */}
          <div className="absolute inset-0">
            <Image
              src="/assets/images/zenobia-wreck-dive-larnaca-cyprus-divers-1777008913772.jpg"
              alt="Divers exploring the Zenobia wreck off Larnaca Cyprus — one of the world's top wreck dives"
              fill
              className="object-cover"
              priority
            />
            {/* Multi-stop gradient overlay */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to top, rgba(7,17,28,0.98) 0%, rgba(7,17,28,0.75) 40%, rgba(7,17,28,0.35) 70%, rgba(7,17,28,0.15) 100%)',
              }}
            />
            {/* Subtle teal tint at bottom */}
            <div
              className="absolute bottom-0 left-0 right-0 h-64"
              style={{
                background: 'linear-gradient(to top, rgba(111,211,224,0.06) 0%, transparent 100%)',
              }}
            />
          </div>

          {/* Hero content */}
          <div className="relative z-10 w-full container mx-auto px-4 pb-16 lg:pb-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8" style={{ color: TEXT_SECONDARY }}>
              <Link href="/dive-sites" className="hover:text-cyan-300 transition-colors">Dive Sites</Link>
              <span style={{ color: BORDER }}>›</span>
              <Link href="/dive-sites/larnaca" className="hover:text-cyan-300 transition-colors">Larnaca</Link>
              <span style={{ color: BORDER }}>›</span>
              <span style={{ color: CYAN }}>Zenobia Wreck</span>
            </nav>

            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5 tracking-wider uppercase"
              style={{ background: 'rgba(255,138,61,0.15)', border: '1px solid rgba(255,138,61,0.35)', color: ORANGE }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true"><path d="M3 17l1.5-6H20l1.5 6"/><path d="M3 17h18"/><path d="M5 17l-1 3h16l-1-3"/><path d="M8 11V7h8v4"/><path d="M12 7V4"/><path d="M10 4h4"/></svg> World-Class Wreck Dive
            </div>

            {/* Title */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold mb-5 leading-tight"
              style={{ color: TEXT_PRIMARY, textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
            >
              Zenobia Wreck Diving<br />
              <span style={{ color: CYAN }}>in Cyprus</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl max-w-2xl mb-10 leading-relaxed" style={{ color: TEXT_SECONDARY }}>
              One of the world's top wreck dives — a 172m ferry resting on her side off Larnaca,
              packed with trucks, marine life, and dive routes for all levels.
            </p>

            {/* Quick info badges */}
            <div className="flex flex-wrap gap-3">
              {QUICK_BADGES?.map((b) => (
                <div
                  key={b?.label}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    background: 'rgba(15,34,52,0.85)',
                    border: `1px solid ${BORDER}`,
                    backdropFilter: 'blur(12px)',
                    color: TEXT_PRIMARY,
                  }}
                >
                  <span className="text-base">{b?.icon}</span>
                  <span style={{ color: TEXT_SECONDARY }} className="text-xs">{b?.label}:</span>
                  <span style={{ color: CYAN }} className="font-semibold">{b?.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 2. OVERVIEW ─────────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                About the Zenobia
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <p className="text-lg leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                The Zenobia wreck is Cyprus's most famous dive site and one of the top wreck dives in the world.
                This Swedish-built ferry sank in 1980 and now lies on her side off the coast of Larnaca.
              </p>
              <p className="text-lg leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                With depths suitable for both recreational and advanced divers, the site offers everything from
                external exploration to penetration dives through cargo decks filled with trucks. Over time, it
                has developed into a thriving artificial reef with rich marine life.
              </p>
            </div>
          </div>
        </section>

        {/* ── 3. WHY DIVE ZENOBIA ─────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-headline font-bold mb-3" style={{ color: TEXT_PRIMARY }}>
                Why Dive Zenobia?
              </h2>
              <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                Five reasons this wreck earns its legendary status
              </p>
            </div>

            {/* Asymmetric bento layout — 2 large + 3 smaller */}
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
              {WHY_DIVE?.slice(0, 2)?.map((item) => (
                <div
                  key={item?.title}
                  className="md:col-span-1 rounded-2xl p-7 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  <span
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{item?.icon}</span>
                  <h3 className="text-lg font-headline font-semibold" style={{ color: TEXT_PRIMARY }}>{item?.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{item?.desc}</p>
                </div>
              ))}
              {/* Span 1 col on md */}
              <div
                className="md:col-span-1 rounded-2xl p-7 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1"
                style={{
                  background: `linear-gradient(135deg, rgba(111,211,224,0.08) 0%, ${BG_CARD} 100%)`,
                  border: `1px solid rgba(111,211,224,0.2)`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}
              >
                <span
                  className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ background: 'rgba(111,211,224,0.15)', color: CYAN }}
                >{WHY_DIVE?.[2]?.icon}</span>
                <h3 className="text-lg font-headline font-semibold" style={{ color: CYAN }}>{WHY_DIVE?.[2]?.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{WHY_DIVE?.[2]?.desc}</p>
              </div>
              {WHY_DIVE?.slice(3)?.map((item) => (
                <div
                  key={item?.title}
                  className="rounded-2xl p-7 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  }}
                >
                  <span
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{item?.icon}</span>
                  <h3 className="text-lg font-headline font-semibold" style={{ color: TEXT_PRIMARY }}>{item?.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{item?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4. DIVE PROFILE ─────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                Dive Profile
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {[
                { label: 'Depth Range', value: '16m – 42m', sub: 'Shallow hull to propeller' },
                { label: 'Typical Dive Route', value: 'Descent line → Cargo decks → Optional deeper sections → Ascent', sub: null },
                { label: 'Bottom Time', value: '35–50 minutes', sub: 'Depending on depth profile' },
                { label: 'Conditions', value: 'Calm, occasional mild current, high visibility', sub: null },
              ]?.map((item) => (
                <div
                  key={item?.label}
                  className="rounded-2xl p-6 flex flex-col gap-2"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: CYAN }}>{item?.label}</span>
                  <span className="text-xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>{item?.value}</span>
                  {item?.sub && <span className="text-sm" style={{ color: TEXT_SECONDARY }}>{item?.sub}</span>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 5. DIFFICULTY & CERTIFICATION ───────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-headline font-bold mb-3" style={{ color: TEXT_PRIMARY }}>
                Difficulty & Certification
              </h2>
              <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                The Zenobia caters to every level — from first-time wreck divers to technical specialists
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {CERT_CARDS?.map((card) => (
                <div
                  key={card?.level}
                  className="rounded-2xl p-7 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${card?.color}30`,
                    boxShadow: `0 4px 24px ${card?.color}10`,
                  }}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                      style={{ background: `${card?.color}18`, color: card?.color }}
                    >{card?.icon}</span>
                    <h3 className="text-xl font-headline font-bold" style={{ color: card?.color }}>{card?.level}</h3>
                  </div>
                  <p className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>{card?.desc}</p>
                  <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>{card?.detail}</p>
                </div>
              ))}
            </div>

            {/* Safety note */}
            <div
              className="rounded-xl px-6 py-4 flex items-start gap-3"
              style={{
                background: 'rgba(255,138,61,0.08)',
                border: '1px solid rgba(255,138,61,0.25)',
              }}
            >
              <span
                className="flex-shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(255,138,61,0.15)', color: ORANGE }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
              <p className="text-sm leading-relaxed" style={{ color: TEXT_SECONDARY }}>
                <strong style={{ color: ORANGE }}>Penetration diving</strong> requires proper training and should only be done with qualified, locally certified guides. Always discuss your certification level with your dive centre before the dive.
              </p>
            </div>
          </div>
        </section>

        {/* ── 6. MARINE LIFE ──────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                Marine Life
              </h2>
            </div>
            <p className="text-base mb-8 max-w-2xl" style={{ color: TEXT_SECONDARY }}>
              Over four decades, the Zenobia has transformed into one of the Mediterranean's most productive artificial reefs. Expect encounters with:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {MARINE_LIFE?.map((species) => (
                <div
                  key={species?.name}
                  className="flex items-center gap-3 rounded-xl px-5 py-4"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <span
                    className="w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{species?.icon}</span>
                  <span className="font-medium text-sm" style={{ color: TEXT_PRIMARY }}>{species?.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. HIGHLIGHTS ───────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-headline font-bold mb-3" style={{ color: TEXT_PRIMARY }}>
                Dive Highlights
              </h2>
              <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                Five unmissable features of the Zenobia dive experience
              </p>
            </div>

            {/* Horizontal scroll on mobile, grid on desktop */}
            <div className="flex gap-5 overflow-x-auto pb-4 md:grid md:grid-cols-5 md:overflow-visible"
              style={{ scrollSnapType: 'x mandatory' }}>
              {HIGHLIGHTS?.map((card, idx) => (
                <div
                  key={card?.title}
                  className="relative flex-shrink-0 w-64 md:w-auto rounded-2xl overflow-hidden cursor-pointer"
                  style={{
                    aspectRatio: '3/4',
                    scrollSnapAlign: 'start',
                    boxShadow: hoveredHighlight === idx ? `0 12px 40px rgba(111,211,224,0.2)` : '0 4px 20px rgba(0,0,0,0.4)',
                    transition: 'box-shadow 0.3s ease',
                  }}
                  onMouseEnter={() => setHoveredHighlight(idx)}
                  onMouseLeave={() => setHoveredHighlight(null)}
                >
                  <Image
                    src={card?.image}
                    alt={card?.alt}
                    fill
                    className="object-cover"
                    style={{
                      transform: hoveredHighlight === idx ? 'scale(1.06)' : 'scale(1)',
                      transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    }}
                  />
                  {/* Gradient overlay */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(to top, rgba(7,17,28,0.92) 0%, rgba(7,17,28,0.3) 50%, transparent 100%)',
                    }}
                  />
                  {/* Card content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-base font-headline font-bold mb-1" style={{ color: TEXT_PRIMARY }}>{card?.title}</h3>
                    <p className="text-xs leading-relaxed" style={{ color: TEXT_SECONDARY }}>{card?.desc}</p>
                  </div>
                  {/* Number badge */}
                  <div
                    className="absolute top-4 left-4 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: 'rgba(111,211,224,0.15)', border: `1px solid ${CYAN}40`, color: CYAN }}
                  >
                    {idx + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 8. PRACTICAL INFO ───────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 rounded-full" style={{ background: `linear-gradient(to bottom, ${CYAN}, ${ORANGE})` }} />
              <h2 className="text-3xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                Practical Information
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PRACTICAL_INFO?.map((item) => (
                <div
                  key={item?.label}
                  className="rounded-2xl p-6 flex items-start gap-4"
                  style={{
                    background: BG_CARD,
                    border: `1px solid ${BORDER}`,
                  }}
                >
                  <span
                    className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5"
                    style={{ background: 'rgba(111,211,224,0.10)', color: CYAN }}
                  >{item?.icon}</span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: CYAN }}>{item?.label}</p>
                    <p className="text-sm font-medium leading-relaxed" style={{ color: TEXT_PRIMARY }}>{item?.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. SAFETY & TIPS ────────────────────────────────────────────── */}
        <section className="py-20" style={{ background: BG_DARK }}>
          <div className="container mx-auto px-4 max-w-3xl">
            <div
              className="rounded-2xl p-8 md:p-10"
              style={{
                background: 'linear-gradient(135deg, rgba(111,211,224,0.06) 0%, rgba(15,34,52,0.8) 100%)',
                border: `1px solid rgba(111,211,224,0.18)`,
              }}
            >
              <div className="flex items-center gap-3 mb-7">
                <span
                  className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0"
                  style={{ background: 'rgba(111,211,224,0.12)', color: CYAN }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </span>
                <h2 className="text-2xl font-headline font-bold" style={{ color: TEXT_PRIMARY }}>
                  Safety & Tips
                </h2>
              </div>
              <ul className="space-y-4">
                {SAFETY_TIPS?.map((tip) => (
                  <li key={tip} className="flex items-start gap-3">
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${CYAN}20`, color: CYAN, border: `1px solid ${CYAN}40` }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                    <span className="text-base leading-relaxed" style={{ color: TEXT_SECONDARY }}>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── 10. CTA ─────────────────────────────────────────────────────── */}
        <section className="py-24" style={{ background: BG_SECTION }}>
          <div className="container mx-auto px-4 max-w-3xl text-center">
            {/* Hero image strip */}
            <div className="relative w-full h-48 rounded-2xl overflow-hidden mb-10">
              <Image
                src="/assets/images/zenobia-wreck-cyprus-scuba-diving-larnaca-1777197346785.jpg"
                alt="Aerial view of the Zenobia wreck site off Larnaca Cyprus with divers in the water"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(14,28,43,0.9) 0%, rgba(14,28,43,0.3) 100%)' }} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <h2 className="text-3xl md:text-4xl font-headline font-bold mb-2" style={{ color: TEXT_PRIMARY }}>
                  Ready to Dive the Zenobia?
                </h2>
                <p className="text-base" style={{ color: TEXT_SECONDARY }}>
                  Explore more Larnaca dive sites or find a dive centre to book your trip
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/dive-sites/larnaca"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: `linear-gradient(135deg, ${CYAN} 0%, #4bbfcc 100%)`,
                  color: '#07111C',
                  boxShadow: `0 4px 20px rgba(111,211,224,0.3)`,
                }}
              >
                <span>🗺️</span>
                Explore more Larnaca dive sites
              </Link>
              <Link
                href="/dive-centres-directory"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 hover:-translate-y-0.5"
                style={{
                  background: 'transparent',
                  color: TEXT_PRIMARY,
                  border: `1px solid rgba(111,211,224,0.3)`,
                }}
              >
                <span>🏪</span>
                Find dive centres in Larnaca
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
